import { useEffect, useState } from 'react';
import { ErrorMessage, Field, useField, FieldAttributes } from 'formik';
import cn from 'classnames';
import { Storage } from 'aws-amplify';

import { IconDownChevron } from './Icons';

export function InputGroup({ name, label, as, ...props }: FieldAttributes<any>) {
  return (
    <div className="mb-6">
      {label && (
        <label
          htmlFor={name}
          className="block text-xs font-semibold tracking-wider text-gray-800 uppercase"
        >
          {label}
        </label>
      )}
      <div className="mt-0.5">
        <Field
          name={name}
          className={cn(
            'block w-full px-0',
            'border-0 border-b border-gray-400 focus:ring-0 focus:border-blue-500',
          )}
          type={as ? undefined : 'text'}
          as={as}
          {...props}
        />
        <ErrorMessage name={name} component="div" className="mt-1 text-sm text-red-500" />
      </div>
    </div>
  );
}

export function InputSelect({
  name,
  label,
  placeholder = '',
  children,
}: {
  name: string;
  label: string;
  placeholder?: string;
  children: any;
}) {
  const [field] = useField(name);

  return (
    <div className="mb-6">
      <label
        htmlFor={name}
        className="block text-xs font-semibold tracking-wider text-gray-800 uppercase"
      >
        {label}
      </label>
      <div className="mt-0.5">
        <Field
          name={name}
          className={cn(
            'block w-full px-0',
            'border-0 border-b border-gray-400 focus:ring-0 focus:border-blue-500',
            { 'text-gray-500': !field.value && placeholder },
          )}
          as="select"
        >
          {placeholder && (
            <option className="text-gray-200" value="">
              {placeholder}
            </option>
          )}
          {children}
        </Field>
        <ErrorMessage name={name} component="div" className="mt-1 text-sm text-red-500" />
      </div>
    </div>
  );
}

export function InputImage(props: any) {
  const {
    children,
    defaultSrc: defaultValue,
    storagePrefix,
    shape = 'circle',
    ...restProps
  } = props;
  const [field, meta, helpers] = useField(restProps);
  const { value, ...fieldProps } = field;
  const [src, setSrc] = useState<string>('');

  // get the signed url for the filepath value that the input has saved or received as a default value
  useEffect(() => {
    const filepath = value || defaultValue;
    if (filepath) {
      Storage.get(filepath)
        .then((signedPath) => {
          // aws-amplify return ts type is incorrectly set to Object
          setSrc(signedPath as unknown as string);
        })
        .catch((err) => {
          helpers.setError(err.name);
        });
    }
  }, [value, defaultValue, helpers]);

  // eslint-disable-next-line consistent-return
  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e?.target?.files?.[0];
    if (!file) return helpers.setValue('');

    try {
      // creates file and gets a signed url for the image
      const filepath = `${storagePrefix ? `${storagePrefix}/` : ''}${file.name}`;
      await Storage.put(filepath, file, { contentType: file.type });
      helpers.setValue(filepath);
    } catch (err) {
      const error = err as Error;
      helpers.setError(error.name);
    }
  };

  return (
    <div className="flex items-center">
      <div
        className={cn('relative flex-shrink-0 mr-6 overflow-hidden w-28 h-28', {
          'rounded-full': shape === 'circle',
          'rounded-xl': shape === 'square',
        })}
      >
        {src ? (
          <img className="w-full h-full" src={src} alt={fieldProps.name} />
        ) : (
          <div className="flex items-center justify-center w-full h-full border-2 border-blue-500 rounded-full text-7xl">
            {children}
          </div>
        )}
      </div>
      <div>
        <div className="flex">
          <label
            htmlFor={fieldProps.name}
            className="flex item-center border-2 border-blue-500 rounded-lg text-blue-500 px-2.5 p-1 mb-2"
          >
            Upload Image <IconDownChevron className="inline w-6 h-6 ml-1" />
          </label>
          <input
            {...fieldProps}
            {...restProps}
            id={fieldProps.name}
            className="hidden"
            type="file"
            text="Upload Image"
            accept=".jpg,.jpeg,.png,.gif"
            onChange={handleFileChange}
          />
        </div>
        <p className="text-sm text-gray-400">Allowed image formats: .jpg, .png, .gif</p>
        {meta.error && meta.touched && <div>{meta.error}</div>}
      </div>
    </div>
  );
}

interface InputCheckboxProps {
  name: string;
  label: any;
  value: string;
  checked?: boolean;
  onChange?: () => void;
}

export function InputCheckbox({ label, ...inputProps }: InputCheckboxProps) {
  return (
    <div className="mb-6">
      <div className="relative flex items-start">
        <div className="flex items-center h-5">
          <Field
            type="checkbox"
            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            id={inputProps.name}
            {...inputProps}
          />
        </div>
        <div className="ml-3 text-sm">
          <label htmlFor="comments" className="font-medium text-gray-700">
            {label}
          </label>
        </div>
      </div>
      <ErrorMessage name={inputProps.name} component="div" className="mt-1 text-sm text-red-500" />
    </div>
  );
}
